import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, CheckCircle2, CloudOff, Info, Loader, PlayCircle, WifiOff } from 'lucide-react';
import Charting from '../../patient/charting';
import Assessments from '../../patient/assessments/assessments';
import StartVisitSheet, { StartVisitShiftInfo } from '../../visit/StartVisitSheet';
import EndVisitSheet, { endVisitErrorMessage, formatElapsed } from '../../visit/EndVisitSheet';
import type { EndVisitSignoff } from '../../../dtos/patientmr/visit.dto';
import { describeApiError, getApiErrorCode } from '../../../utils/apiErrors';
import Sheet from '../../common/Sheet';
import { useVisitActions } from '../../visit/useVisitActions';
import { useAuth } from '../../../context/authcontext';
import { useDeviceClass, useFieldMode, useOnlineStatus } from '../../../hooks/useOfflineDevice';
import { useTwoPane } from '../../../hooks/useTwoPane';
import { Packets, PacketVisitContext } from '../../../services/offline/packetService';
import { OfflineSession } from '../../../services/offline/offlineSession';
import { Offline } from '../../../services/offline/offlineServices';
import { OUTBOX_CHANGED_EVENT } from '../../../services/offline/outbox';
import { fetchVisitById, getProviderActiveVisit, isVisitInProgress, localVisitToVisit, StartVisitPayload, VisitWithSync } from '../../../services/patientmr/visits.service';
import { submitChart } from '../../../services/patientmr/charting.service';
import { getCurrentCoords, visitLocationPayload } from '../../../utils/visitStart';
import { isApiError } from '../../../dtos/common/api.dto';
import type { PacketShiftDto, PacketHeaderDto } from '../../../dtos/offline/offline.dto';
import { allergyLabels, precautionLabels } from '../OfflineHome';
import VisitActionBar, { visitStage } from '../../visit/VisitActionBar';
import { DownloadVisitNoteButton } from '../../patient/charting/components/VisitNoteActions';

export interface OfflineVisitWorkspaceProps {
  patientId: string;
  /** An existing visit (local or server); absent = start mode for `shiftId` */
  visitId?: string | null;
  shiftId?: string | null;
  onExit: () => void;
  /** A visit was started here (the host updates its route) */
  onVisitStarted?: (visitId: string) => void;
}

type Coords = { latitude: number; longitude: number } | null;

const fullName = (h?: PacketHeaderDto) => (h ? [h.preferredName || h.firstName, h.lastName].filter(Boolean).join(' ') : '') || 'Patient';
const hhmm = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export function shiftInfo(s: PacketShiftDto): StartVisitShiftInfo {
  return {
    id: s.shiftId,
    scheduleRef: s.scheduleRef || null,
    startTime: hhmm(s.start),
    endTime: hhmm(s.end),
    overnight: s.overnight ?? new Date(s.start).toDateString() !== new Date(s.end).toDateString(),
    visitType: s.visitType || 'Routine',
  };
}

const NURSING = new Set(['RN', 'LVN', 'LPN', 'NURSE']);

/** "4 y" / "7 mo" from a YYYY-MM-DD date of birth ('' when it cannot be read) */
export const ageLabel = (dob?: string | null, now = new Date()): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dob || ''));
  if (!m) return '';
  const birth = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  let months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  if (now.getDate() < birth.getDate()) months -= 1;
  if (months < 0) return '';
  return months < 24 ? `${months} mo` : `${Math.floor(months / 12)} y`;
};

const errorText = (e: unknown, fallback: string) =>
  (isApiError(e) && e.statusCode >= 400 && e.statusCode < 500) || (e instanceof Error && e.name !== 'ApiError') ? (e as Error).message || fallback : fallback;

/**
 * Offline Visit Workspace (Phase 16C C6, decision X6, route /visit/:visitId). One focused screen
 * that runs a whole visit with or without signal: start → chart → assessment → patient
 * signature → end → submit. It reuses the chart, the assessments and the start / end sheets;
 * the services decide whether each write goes online or into the encrypted outbox.
 * Header: patient, DOB and allergies first (clinical safety), visit timer, EVV note, sync state.
 */
const OfflineVisitWorkspace: React.FC<OfflineVisitWorkspaceProps> = ({ patientId, visitId: initialVisitId, shiftId, onExit, onVisitStarted }) => {
  const { user } = useAuth();
  const online = useOnlineStatus();
  const visitActionsHere = useDeviceClass() === 'MOBILE';
  // Phase 18U U18: a landscape tablet charts side by side, so the column is wider
  const twoPane = useTwoPane(useFieldMode(true));
  const actions = useVisitActions();
  const [visitId, setVisitId] = useState<string | null>(initialVisitId || null);
  const [ctx, setCtx] = useState<PacketVisitContext | null>(null);
  const [visit, setVisit] = useState<VisitWithSync | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [, setTick] = useState(0);

  // start
  const [startOpen, setStartOpen] = useState(false);
  const [coords, setCoords] = useState<Promise<Coords> | null>(null);
  const [locationStatus, setLocationStatus] = useState<'locating' | 'ok' | 'unavailable'>('locating');
  const [clockIn, setClockIn] = useState<'ok' | 'missed_prompt' | ''>('');
  const [serviceType, setServiceType] = useState<'PDN' | 'SKILLED_NURSING_VISIT'>('PDN');
  // end
  const [endOpen, setEndOpen] = useState(false);
  const [clockOut, setClockOut] = useState<'ok' | 'missed_prompt' | ''>('');
  const [attestOpen, setAttestOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showAssessments, setShowAssessments] = useState(false);
  // Phase 18U U2: MRN, EVV note and location live in "Visit details" to keep the header short
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Phase 18U U10: chart hour headers stick just below this screen's header
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const measure = () => setHeaderHeight(Math.round(el.getBoundingClientRect().height));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const shift = ctx?.shift || null;
  const evvPrompt = Boolean(ctx?.evvRequired ?? visit?.evvRequired);
  const discipline = String(shift?.discipline || ctx?.postVisit?.discipline || (user as { providerType?: string } | null)?.providerType || 'RN');
  const isNursing = NURSING.has(discipline.toUpperCase());

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const unlocked = Boolean(OfflineSession.getStore());
      const packetCtx = unlocked ? await Packets.getVisitContext(patientId, shiftId, visitId) : null;
      setCtx(packetCtx);
      if (visitId) {
        const local = unlocked ? await Offline.visits.get(visitId) : undefined;
        if (local) {
          setVisit(localVisitToVisit(local));
        } else if (packetCtx?.postVisit) {
          const pv = packetCtx.postVisit;
          setVisit({ id: pv.visitId, visitId: pv.visitId, patientId, providerId: '', tenantId: '', startTime: pv.startTime || '', endTime: pv.endTime || pv.endedAt, status: 'DOCPENDING', chartingStatus: pv.chartingStatus, createdAt: '', updatedAt: '' });
        } else if (online) {
          const active = await getProviderActiveVisit();
          setVisit(active && active.id === visitId ? active : await fetchVisitById(patientId, visitId));
        } else {
          setLoadError('This visit is not stored on this device. Reconnect to open it.');
        }
      }
    } catch {
      setLoadError('Could not open this visit. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [patientId, shiftId, visitId, online]);

  useEffect(() => { load(); }, [load]);

  // Keep the sync chip and status current as the outbox drains
  useEffect(() => {
    const onChange = () => {
      if (!visitId || !OfflineSession.getStore()) return;
      Offline.visits.get(visitId).then((v) => { if (v) setVisit(localVisitToVisit(v)); }).catch(() => undefined);
    };
    window.addEventListener(OUTBOX_CHANGED_EVENT, onChange);
    const timer = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => { window.removeEventListener(OUTBOX_CHANGED_EVENT, onChange); clearInterval(timer); };
  }, [visitId]);

  const captureLocation = () => {
    setLocationStatus('locating');
    // No geocoding offline: coordinates only, or none (never 0,0)
    const p = getCurrentCoords().then((c) => { setLocationStatus(c ? 'ok' : 'unavailable'); return c; });
    setCoords(p);
    return p;
  };

  const openStart = () => {
    setActionError(null);
    setClockIn('');
    captureLocation();
    setStartOpen(true);
  };

  const confirmStart = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const c = coords ? await coords : null;
      const payload: StartVisitPayload = {
        patientId,
        discipline,
        ...visitLocationPayload(c),
        visitType: shift?.visitType || 'Routine',
        startTime: new Date().toISOString(),
        notes: '',
        ...(shiftId ? { scheduleId: shiftId } : {}),
        ...(shift?.authorizationId ? { authorizationId: shift.authorizationId, authorizationNumber: shift.authorizationNumber } : {}),
        ...(evvPrompt && clockIn ? { evvClockInFlag: clockIn } : {}),
        ...(isNursing ? { serviceType } : {}),
      };
      const started = await actions.start(payload, { patientName: fullName(ctx?.packet.header), evvRequired: evvPrompt });
      const id = started.id || (started as VisitWithSync).visitId || '';
      setStartOpen(false);
      setVisitId(id);
      setVisit(started as VisitWithSync);
      onVisitStarted?.(id);
    } catch (e) {
      setActionError(errorText(e, 'The visit could not be started.'));
    } finally {
      setBusy(false);
    }
  };

  const openEnd = () => {
    setActionError(null);
    setClockOut('');
    captureLocation();
    setEndOpen(true);
  };

  const confirmEnd = async (signoff: EndVisitSignoff) => {
    if (!visitId) return;
    setBusy(true);
    setActionError(null);
    try {
      const c = coords ? await coords : null;
      const loc = visitLocationPayload(c);
      const ended = await actions.end(patientId, visitId, {
        ...signoff,
        ...(evvPrompt && clockOut ? { evvClockOutFlag: clockOut } : {}),
        ...(loc.location ? { location: loc.location } : {}),
      });
      setVisit((v) => ({ ...(v || (ended as VisitWithSync)), ...(ended as VisitWithSync) }));
      setEndOpen(false);
      setAttestOpen(true);
    } catch (e) {
      // Phase 18 M4: CHART_EMPTY / SIGNER_REQUIRED have their own text
      setActionError(getApiErrorCode(e) ? endVisitErrorMessage(e, 'The visit could not be ended.') : errorText(e, 'The visit could not be ended.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmSubmit = async () => {
    if (!visitId) return;
    setBusy(true);
    setActionError(null);
    try {
      await submitChart(patientId, visitId);
      setAttestOpen(false);
      setSubmitted(true);
    } catch (e) {
      // Domain codes (VISIT_SUMMARY_REQUIRED, SUPERVISORY_NOTE_REQUIRED, ...) get their own text
      setActionError(getApiErrorCode(e) ? describeApiError(e, 'The chart could not be submitted.') : errorText(e, 'The chart could not be submitted.'));
    } finally {
      setBusy(false);
    }
  };

  const header = ctx?.packet.header;
  const allergies = useMemo(() => allergyLabels(ctx?.packet.allergies), [ctx]);
  const precautions = useMemo(() => precautionLabels(ctx?.packet.precautions), [ctx]);
  const name = header ? fullName(header) : visit?.patientName || 'Patient';
  const inProgress = isVisitInProgress(visit);
  const unsynced = visit?.syncState && visit.syncState !== 'synced';
  const visitWindow = useMemo(() => ({ startedAt: visit?.startTime || null, endedAt: visit?.endTime || null }), [visit?.startTime, visit?.endTime]);

  return (
    <div className="min-h-full bg-gray-50" data-testid="visit-workspace" style={{ ['--chart-sticky-top' as string]: `${headerHeight}px` } as React.CSSProperties}>
      <header ref={headerRef} className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-3">
        <div className="flex items-start gap-2">
          <button type="button" onClick={onExit} aria-label="Back" className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-gray-200">
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold text-gray-900">{name}</h1>
            <p className="text-sm text-gray-600">{[ageLabel(header?.dateOfBirth), header?.dateOfBirth && `DOB ${header.dateOfBirth}`].filter(Boolean).join(' · ') || (online ? '' : 'Offline')}</p>
          </div>
          {!online && <span className="flex items-center gap-1 rounded-full bg-slate-800 px-2 py-1 text-xs font-semibold text-white"><WifiOff size={12} aria-hidden="true" />Offline</span>}
          {unsynced && <span data-testid="visit-not-synced" className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900"><CloudOff size={12} aria-hidden="true" />Not synced</span>}
          <button type="button" onClick={() => setDetailsOpen(true)} aria-label="Visit details" className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-gray-200 text-gray-700">
            <Info size={18} aria-hidden="true" />
          </button>
        </div>
        {/* Clinical safety first (design §6.5) */}
        <div className="mt-2 text-sm" aria-label="Allergies">
          {ctx?.packet.allergiesNoneKnown && !allergies.length ? <span className="text-gray-700">No known allergies</span>
            : allergies.length ? <span className="font-semibold text-red-800"><AlertTriangle size={14} className="mr-1 inline" aria-hidden="true" />Allergies: {allergies.join(', ')}</span>
              : ctx ? <span className="text-gray-500">Allergies not recorded</span> : null}
          {precautions.length > 0 && <div className="text-amber-900">Precautions: {precautions.join(', ')}</div>}
        </div>
        {visit?.startTime && (
          <p className="mt-1 text-sm text-gray-700">
            {inProgress ? `In visit · ${formatElapsed(visit.startTime)}` : `Visit ${new Date(visit.startTime).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}${visit.endTime ? ` – ${new Date(visit.endTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}`}
            {visit.gpsDenied ? ' · location not recorded' : ''}
          </p>
        )}
      </header>

      {/* pb: room for the sticky action bar (U2) */}
      <main className={`mx-auto ${twoPane ? 'max-w-6xl' : 'max-w-3xl'} space-y-4 px-4 pb-32 pt-4`}>
        {loading && <p role="status" className="flex items-center gap-2 text-sm text-gray-600"><Loader size={16} className="animate-spin" />Opening visit…</p>}
        {loadError && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {loadError} <button type="button" className="ml-2 underline" onClick={load}>Try again</button>
          </div>
        )}
        {actionError && !startOpen && !endOpen && !attestOpen && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{actionError}</div>}

        {!loading && !loadError && !visitId && (
          <section aria-label="Start visit" className="rounded-xl border border-gray-200 bg-white p-4">
            {shift ? <p className="text-sm text-gray-700">Shift {new Date(shift.start).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })} – {new Date(shift.end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}{shift.serviceType ? ` · ${shift.serviceType}` : ''}</p>
              : <p className="text-sm text-gray-600">No shift selected. The office matches the visit to a shift when it syncs.</p>}
            {visitActionsHere ? (
              <button type="button" onClick={openStart} className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-emerald-700 text-base font-semibold text-white">
                <PlayCircle size={20} aria-hidden="true" />Start visit
              </button>
            ) : <p className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">Start this visit on your phone or tablet.</p>}
          </section>
        )}

        {visitId && visit && !loading && (
          <>
            {submitted || (visit.chartingStatus && visit.chartingStatus !== 'DRAFT') ? (
              <section className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" role="status">
                <CheckCircle2 size={20} className="shrink-0" aria-hidden="true" />
                <div>
                  <div className="font-semibold">Chart submitted{unsynced ? ' — not synced yet' : ''}</div>
                  <div>{unsynced ? 'It is saved encrypted on this device and is sent when you are back online.' : 'The office has it.'}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {online && !unsynced && <DownloadVisitNoteButton patientId={patientId} visitId={visitId} />}
                    <button type="button" onClick={onExit} className="min-h-[44px] rounded-lg border border-emerald-700 bg-white px-4 font-semibold text-emerald-800">Back to My Day</button>
                  </div>
                </div>
              </section>
            ) : null}

            <Charting
              expanded
              toggleExpanded={() => undefined}
              patientId={patientId}
              activeVisitId={visitId}
              visitWindow={visitWindow}
              workspace
              renderActionBar={(openAdd, canAdd) => (
                <VisitActionBar
                  stage={visitStage({ hasVisit: true, inProgress, chartingStatus: visit.chartingStatus, submitted, canEndHere: visitActionsHere })}
                  onAdd={openAdd}
                  addDisabled={!canAdd}
                  onEnd={openEnd}
                  onSign={() => { setActionError(null); setAttestOpen(true); }}
                  onDone={onExit}
                  busy={busy}
                />
              )}
            />

            <section className="rounded-xl border border-gray-200 bg-white">
              <button type="button" aria-expanded={showAssessments} onClick={() => setShowAssessments((s) => !s)} className="flex min-h-[48px] w-full items-center justify-between px-4 text-left text-sm font-semibold text-gray-800">
                Assessments <span className="text-xs font-normal text-gray-500">{showAssessments ? 'Hide' : 'Show'}</span>
              </button>
              {showAssessments && (
                <div className="border-t border-gray-100">
                  <Assessments expanded toggleExpanded={() => undefined} patientId={patientId} visitId={visitId} />
                </div>
              )}
            </section>

            <section aria-label="Needs connection" className="rounded-xl border border-gray-200 bg-white p-4 text-sm">
              <h2 className="mb-2 font-semibold text-gray-800">Medications</h2>
              {ctx?.packet.medications.length ? (
                <ul className="mb-3 space-y-1 text-gray-700">{ctx.packet.medications.map((m, i) => <li key={m.medicationId || `${m.name}-${i}`}>{m.name}{m.prn ? ' (PRN)' : ''} {[m.dose, m.route, m.frequency].filter(Boolean).join(' · ')}</li>)}</ul>
              ) : <p className="mb-3 text-gray-500">{ctx ? 'No active medications' : 'Open the patient chart to see medications.'}</p>}
              <div className="flex flex-wrap gap-2">
                {['Edit medication list', 'Correct visit time', 'Upload documents'].map((label) => (
                  <button key={label} type="button" disabled={!online} onClick={onExit} className="min-h-[44px] rounded-lg border border-gray-300 px-3 text-gray-700 disabled:cursor-not-allowed disabled:opacity-60">
                    {label}{!online ? ' · Needs connection' : ''}
                  </button>
                ))}
              </div>
            </section>

          </>
        )}
      </main>

      <Sheet open={detailsOpen} onClose={() => setDetailsOpen(false)} title="Visit details" subtitle={name} variant="bottom">
        <dl className="space-y-2 text-sm text-gray-800">
          {header?.mrn && <div><dt className="text-xs font-semibold text-gray-600">MRN</dt><dd>{header.mrn}</dd></div>}
          {header?.dateOfBirth && <div><dt className="text-xs font-semibold text-gray-600">Date of birth</dt><dd>{header.dateOfBirth}</dd></div>}
          {visit?.startTime && (
            <div><dt className="text-xs font-semibold text-gray-600">Visit</dt><dd>{new Date(visit.startTime).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}{visit.endTime ? ` – ${new Date(visit.endTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ' · in progress'}</dd></div>
          )}
          {visit?.gpsDenied && <div><dt className="text-xs font-semibold text-gray-600">Location</dt><dd>Not recorded for this visit</dd></div>}
          {evvPrompt && <div><dt className="text-xs font-semibold text-gray-600">EVV</dt><dd>Clock in and out on HHAeXchange as well.</dd></div>}
        </dl>
      </Sheet>
      <StartVisitSheet
        open={startOpen}
        onClose={() => setStartOpen(false)}
        patientName={name}
        shift={shift ? shiftInfo(shift) : null}
        pendingAdmission={false}
        visitType=""
        onVisitTypeChange={() => undefined}
        pas={[]}
        loadingPas={false}
        selectedPaId={null}
        onSelectPa={() => undefined}
        isNursing={isNursing}
        serviceType={serviceType}
        onServiceTypeChange={(v) => setServiceType(v)}
        evvPrompt={evvPrompt}
        evvRequired={evvPrompt}
        clockIn={clockIn}
        onClockIn={setClockIn}
        location={{ status: locationStatus }}
        error={startOpen ? actionError : null}
        busy={busy}
        onConfirm={confirmStart}
      />
      <EndVisitSheet
        open={endOpen}
        onClose={() => setEndOpen(false)}
        patientName={name}
        startedAt={visit?.startTime || null}
        patientId={patientId}
        visitId={visitId}
        evvPrompt={evvPrompt}
        clockOut={clockOut}
        onClockOut={setClockOut}
        error={endOpen ? actionError : null}
        busy={busy}
        onConfirm={confirmEnd}
      />
      <Sheet
        open={attestOpen}
        onClose={() => !busy && setAttestOpen(false)}
        title="Submit chart"
        subtitle={name}
        footer={(
          <div className="flex gap-2">
            <button type="button" onClick={() => setAttestOpen(false)} disabled={busy} className="min-h-[48px] flex-1 rounded-lg border border-gray-300 text-base font-medium text-gray-700">Later</button>
            <button type="button" onClick={confirmSubmit} disabled={busy} className="flex min-h-[48px] flex-[2] items-center justify-center gap-2 rounded-lg bg-slate-800 text-base font-semibold text-white disabled:opacity-50">
              {busy ? <><Loader size={18} className="animate-spin" />Submitting…</> : 'I attest — submit'}
            </button>
          </div>
        )}
      >
        <p className="text-sm text-gray-800">I attest that this documentation is accurate and complete and describes care I provided.</p>
        {!online && <p className="mt-2 text-sm text-amber-800">You are offline. The chart is submitted when this device syncs.</p>}
        {attestOpen && actionError && <p role="alert" className="mt-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">{actionError}</p>}
      </Sheet>
    </div>
  );
};

export default OfflineVisitWorkspace;
